import { ConfigurationType } from "@/types/ConfigurationType"
import { IntegrationsType } from "@/types/IntegrationsType"
import { produce } from "../utils/immer"
import { Edge } from "reactflow"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

type IntegrationRecord = IntegrationsType | undefined

type NewIntegrationState = {
  /** Records keyed by integration id. The active record is mirrored on `newIntegration`. */
  byId: Record<string, IntegrationRecord>
  activeId?: string
  newIntegration: IntegrationRecord
}

type NewIntegrationAction = {
  bindIntegration: (id: string) => void
  setNewIntegration: (integration: IntegrationRecord) => void
  setNewDestinationConfig: (destinationConfig: ConfigurationType) => void
  setNewEnrichmentConfig: (enrichmentConfig: ConfigurationType, isEdit: boolean) => void
  deleteEnrichmentConfig: (id: string) => void
  setNewDataMapping: (edges: Edge[]) => void
  updateMultipleFields: (fields: Partial<IntegrationsType>) => void
  setUpdateNewStatus: (status: string) => void
}

const recordId = (integration: IntegrationRecord, fallback?: string) => {
  const id = (integration as { _id?: string; connectorId?: string } | undefined)?._id
    || (integration as { connectorId?: string } | undefined)?.connectorId
    || fallback
  return id ? String(id) : undefined
}

const remember = (state: NewIntegrationState) => {
  const id = recordId(state.newIntegration, state.activeId)
  if (!id || !state.newIntegration) return
  state.activeId = id
  state.byId[id] = state.newIntegration
}

export const useNewIntegrationsStore = create<NewIntegrationAction & NewIntegrationState>()(
  devtools(
    setStore => ({
      byId: {},
      activeId: undefined,
      newIntegration: undefined,
      bindIntegration: id => {
        setStore(
          produce(state => {
            if (state.activeId && state.activeId !== id && state.newIntegration) {
              state.byId[state.activeId] = state.newIntegration
            }
            state.activeId = id
            state.newIntegration = state.byId[id]
          })
        )
      },
      setNewIntegration: integration => {
        setStore(
          produce(state => {
            if (!integration) {
              if (state.activeId) delete state.byId[state.activeId]
              state.newIntegration = undefined
              return
            }
            const id = recordId(integration, state.activeId)
            if (id) state.byId[id] = integration
            const incomingId = recordId(integration)
            if (!state.activeId || !incomingId || incomingId === state.activeId) {
              if (id) state.activeId = id
              state.newIntegration = integration
            }
          })
        )
      },
      setNewDestinationConfig: destinationConfig => {
        setStore(
          produce(state => {
            if (state.newIntegration) {
              state.newIntegration.destination = destinationConfig
              remember(state)
            }
          })
        )
      },
      setNewEnrichmentConfig: (enrichmentConfig, isEdit) => {
        setStore(
          produce(state => {
            if (state.newIntegration) {
              if (!state.newIntegration.enrichment) {
                state.newIntegration.enrichment = []
              }
              if (isEdit) {
                state.newIntegration.enrichment = [
                ...state.newIntegration.enrichment.filter(
                  (enrichment: ConfigurationType) => enrichment.id !== enrichmentConfig.id
                ),
                  enrichmentConfig,
                ]
              } else {
                state.newIntegration.enrichment.push(enrichmentConfig)
              }
              remember(state)
            }
          })
        )
      },
      deleteEnrichmentConfig: (id: string) => {
        setStore(
          produce(state => {
            if (state.newIntegration) {
              state.newIntegration.enrichment = state.newIntegration.enrichment.filter(
                (enrichment: ConfigurationType) => enrichment.id !== id
              )
              remember(state)
            }
          })
        )
      },
      setNewDataMapping: edges => {
        setStore(
          produce(state => {
            if (state.newIntegration) {
              state.newIntegration.dataMapping = edges
              remember(state)
            }
          })
        )
      },
      updateMultipleFields: fields => {
        setStore(
          produce(state => {
            if (state.newIntegration) {
              state.newIntegration = {
                ...state.newIntegration,
                ...fields,
              }
              remember(state)
            }
          })
        )
      },
      setUpdateNewStatus: status => {
        setStore(
          produce(state => {
            if (state.newIntegration) {
              state.newIntegration.status = status
              remember(state)
            }
          })
        )
      },
    }),
    { name: "newIntegration" }
  )
)
